import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "antd";
import axios from "axios";
import { useAuth } from "../../context/auth";
import "../../css/proposta-modal.css";
import {
  PropostaFormProvider,
  buildInitialProposta,
  usePropostaForm,
} from "./context/PropostaFormContext";
import PropostaTabs from "./PropostaTabs";
import PropostaResumo from "./sections/PropostaResumo";
import PropostaProponentes from "./sections/PropostaProponentes";
import PropostaImovelSection from "./sections/PropostaImovel";
import PropostaPagamentosSection from "./sections/PropostaPagamentos";
import { usePropostaToast } from "./hooks/usePropostaToast";
import { usePropostaTheme } from "./hooks/usePropostaTheme";
import { downloadPropostaPdf } from "./utils/downloadPropostaPdf";
import { IProposta, IPropostaKanbanTag, PropostaMode } from "./types/proposta";
import {
  camposInvalidos,
  propostaApiToLocal,
  propostaLocalToApi,
} from "./utils/propostaMappers";
import { getHubStatusTag } from "./utils/propostaConstants";
import PropostaStatusTag from "./components/PropostaStatusTag";

interface ModalPropostaProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
  mode?: PropostaMode;
  id_proposta?: number;
  status?: number;
  kanbanTag?: IPropostaKanbanTag;
}

const NOVA_PROPOSTA_TAG: IPropostaKanbanTag = {
  label: "Nova Proposta",
  color: "#6B7280",
};

export default function ModalProposta({
  open,
  onClose,
  onSuccess,
  mode,
  id_proposta,
  status,
  kanbanTag: kanbanTagProp,
}: ModalPropostaProps) {
  const { user, api } = useAuth();
  const toast = usePropostaToast();
  const { isDark, modalRootClassName, selectPopupClassName } = usePropostaTheme();
  const [idEmpresa, setIdEmpresa] = useState(0);
  const [loadingEmpresa, setLoadingEmpresa] = useState(false);
  const [initialProposta, setInitialProposta] = useState<IProposta | null>(null);
  const [loadingProposta, setLoadingProposta] = useState(false);
  const [resolvedKanbanTag, setResolvedKanbanTag] =
    useState<IPropostaKanbanTag | null>(null);

  const idCorretor = user?.id_corretor;
  const onCloseRef = useRef(onClose);
  const toastRef = useRef(toast);
  const apiRef = useRef(api);
  const bootstrappedSessionRef = useRef<string | null>(null);

  onCloseRef.current = onClose;
  toastRef.current = toast;
  apiRef.current = api;

  const sessionKey = id_proposta
    ? `edit-${id_proposta}`
    : `create-${idCorretor ?? "pending"}`;

  useEffect(() => {
    if (!open) {
      bootstrappedSessionRef.current = null;
      setInitialProposta(null);
      setResolvedKanbanTag(null);
      setIdEmpresa(0);
      setLoadingEmpresa(false);
      setLoadingProposta(false);
      return;
    }

    if (!idCorretor) return;

    // Evita re-bootstrap (ex.: refresh de token / re-render do pai),
    // que desmontava o form e zerava o que o usuário já tinha preenchido.
    if (bootstrappedSessionRef.current === sessionKey) {
      return;
    }

    let cancelled = false;

    const bootstrap = async () => {
      if (id_proposta) {
        setLoadingProposta(true);
        try {
          const corretorRes = await apiRef.current.get(
            `/portal/corretor/${idCorretor}`,
          );
          if (cancelled) return;
          const corretorData = corretorRes.data?.data ?? corretorRes.data;
          setIdEmpresa(
            Number(corretorData?.id_empresa) ||
              Number(corretorData?.empresaid) ||
              0,
          );

          const res = await apiRef.current.get(
            `/sin/proposta/${idCorretor}/${id_proposta}`,
          );
          if (cancelled) return;
          const data = res.data?.data ?? res.data;
          setInitialProposta(propostaApiToLocal(data));
          bootstrappedSessionRef.current = sessionKey;
        } catch (error) {
          console.error("Erro ao carregar proposta:", error);
          if (!cancelled) {
            toastRef.current.error("Erro ao carregar proposta.");
            onCloseRef.current();
          }
        } finally {
          if (!cancelled) setLoadingProposta(false);
        }
        return;
      }

      setLoadingEmpresa(true);
      try {
        const res = await apiRef.current.get(`/portal/corretor/${idCorretor}`);
        if (cancelled) return;
        const data = res.data?.data ?? res.data;
        const empresa =
          Number(data?.id_empresa) || Number(data?.empresaid) || 0;
        setIdEmpresa(empresa);

        if (!empresa) {
          toastRef.current.error(
            "Não foi possível identificar a empresa do corretor. Tente novamente.",
          );
          onCloseRef.current();
          return;
        }

        setInitialProposta(buildInitialProposta(idCorretor, empresa));
        setResolvedKanbanTag(NOVA_PROPOSTA_TAG);
        bootstrappedSessionRef.current = sessionKey;
      } catch (error) {
        console.error("Erro ao buscar corretor:", error);
        if (!cancelled) {
          setIdEmpresa(0);
          toastRef.current.error(
            "Não foi possível identificar a empresa do corretor. Tente novamente.",
          );
          onCloseRef.current();
        }
      } finally {
        if (!cancelled) setLoadingEmpresa(false);
      }
    };

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [open, id_proposta, idCorretor, sessionKey]);

  useEffect(() => {
    if (!open) return;
    if (kanbanTagProp) {
      setResolvedKanbanTag(kanbanTagProp);
      return;
    }
    if (!id_proposta) {
      setResolvedKanbanTag(NOVA_PROPOSTA_TAG);
      return;
    }
    const statusValue = status ?? initialProposta?.status;
    if (statusValue !== undefined && statusValue !== null) {
      const tag = getHubStatusTag(statusValue);
      setResolvedKanbanTag(tag);
    }
  }, [open, id_proposta, kanbanTagProp, status, initialProposta?.status]);

  const isLoading =
    loadingEmpresa || loadingProposta || (!id_proposta && !initialProposta);

  const resolvedStatus = initialProposta?.status ?? status;
  const resolvedMode: PropostaMode = id_proposta
    ? resolvedStatus === -1
      ? "edit"
      : "view"
    : mode || "create";

  const title =
    resolvedMode === "create"
      ? "Nova Proposta"
      : resolvedMode === "view"
        ? "Detalhes da Proposta"
        : "Editar Proposta";

  const kanbanTag = useMemo(() => {
    if (kanbanTagProp) return kanbanTagProp;
    if (!id_proposta) return NOVA_PROPOSTA_TAG;
    return resolvedKanbanTag;
  }, [kanbanTagProp, id_proposta, resolvedKanbanTag]);

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={1100}
      destroyOnClose
      zIndex={10000}
      rootClassName={modalRootClassName}
      styles={{
        body: {
          padding: "16px 24px",
        },
      }}
    >
      {isLoading || !initialProposta ? (
        <div className="proposta-modal-body">
          <div className="loading description" style={{ height: 40, width: "33%", marginBottom: 16 }} />
          <div className="loading" style={{ flex: 1 }} />
        </div>
      ) : (
        <PropostaFormProvider
          key={sessionKey}
          initialProposta={initialProposta}
          initialMode={resolvedMode}
          kanbanTag={kanbanTag}
          isDark={isDark}
          selectPopupClassName={selectPopupClassName}
        >
          <ModalPropostaContent
            onClose={onClose}
            onSuccess={onSuccess}
            idEmpresa={idEmpresa}
          />
        </PropostaFormProvider>
      )}
    </Modal>
  );
}

function ModalPropostaContent({
  onClose,
  onSuccess,
  idEmpresa,
}: {
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
  idEmpresa: number;
}) {
  const { api } = useAuth();
  const { proposta, activeTab, setActiveTab, lockImovelEdit, kanbanTag, isDark } =
    usePropostaForm();
  const toast = usePropostaToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const handleSubmit = async (published: boolean) => {
    const invalids = camposInvalidos(proposta, lockImovelEdit, published);
    if (invalids.length) {
      toast.info(
        "Preencha todos os campos obrigatórios!",
        `${invalids[0].trim()}`,
      );
      return;
    }

    const empresaId = idEmpresa || proposta.id_empresa;
    if (!empresaId) {
      toast.error(
        "Não foi possível identificar a empresa do corretor. Tente novamente.",
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = propostaLocalToApi({
        ...proposta,
        id_empresa: empresaId,
        published,
        is_ppm: null,
        comissao: 4,
      });
      await api.post("/sin/proposta/", payload);

      if (published) {
        toast.success("Proposta enviada com sucesso!");
      } else {
        toast.info("Proposta salva com sucesso!");
      }

      await onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Erro ao salvar proposta:", error);
      if (axios.isAxiosError(error) && error.response?.data?.err) {
        toast.info("Proposta inválida!", String(error.response.data.err));
      } else {
        toast.error(
          published
            ? "Não foi possível enviar a proposta."
            : "Erro ao salvar proposta. Verifique os dados e tente novamente.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!proposta.uuid || isDownloadingPdf) return;
    setIsDownloadingPdf(true);
    try {
      const fileName = proposta.id_proposta
        ? `proposta-${proposta.id_proposta}.pdf`
        : "proposta-digital.pdf";
      await downloadPropostaPdf(api, proposta.uuid, fileName);
      toast.success("PDF baixado com sucesso!");
    } catch (error) {
      console.error("Erro ao baixar PDF:", error);
      toast.error(
        "Não foi possível baixar o PDF",
        "Por favor, tente novamente mais tarde.",
      );
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const renderTab = () => {
    switch (activeTab) {
      case "proponentes":
        return <PropostaProponentes />;
      case "imovel":
        return <PropostaImovelSection />;
      case "pagamentos":
        return <PropostaPagamentosSection />;
      default:
        return (
          <PropostaResumo
            onSave={() => handleSubmit(false)}
            onSend={() => handleSubmit(true)}
            onDownloadPdf={handleDownloadPdf}
            isDownloadingPdf={isDownloadingPdf}
            isSubmitting={isSubmitting}
          />
        );
    }
  };

  return (
    <div className="proposta-modal-body min-w-0">
      <PropostaTabs activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === "resumo" && (
        <div
          style={{
            width: "100%",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            marginBottom: 8,
          }}
        >
          <p
            style={{
              fontSize: 14,
              margin: 0,
              color: isDark ? "#9ca3af" : "#6b7280",
            }}
          >
            {proposta.id_proposta ? `Proposta #${proposta.id_proposta}` : ""}
          </p>
          <PropostaStatusTag tag={kanbanTag} />
        </div>
      )}

      <div className="proposta-modal-scroll min-w-0">{renderTab()}</div>
    </div>
  );
}
